import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { tokens } from '@fluentui/react-components';
import { HeatMapChart } from '@fluentui/react-charting';
import type { IHeatMapChartData, IHeatMapChartDataPoint } from '@fluentui/react-charting';
import { WidgetContainer } from '../WidgetContainer';
import { ChartThemeProvider } from '../ChartThemeProvider';
import { useUtilizationHeatmapStyles } from './UtilizationHeatmap.styles';
import type { HeatmapCell, HeatmapTier } from '@/types/reports';

export interface UtilizationHeatmapProps {
  data: HeatmapCell[];
}

const TIER_CONFIG: { tier: HeatmapTier; label: string; color: string; value: number }[] = [
  { tier: 'low', label: '<30%', color: tokens.colorPaletteGreenBackground3, value: 15 },
  { tier: 'moderate', label: '30–50%', color: tokens.colorPaletteYellowBackground3, value: 40 },
  { tier: 'medium', label: '50–70%', color: tokens.colorPaletteMarigoldBackground3, value: 60 },
  { tier: 'high', label: '70–85%', color: tokens.colorPaletteDarkOrangeBackground3, value: 77 },
  { tier: 'critical', label: '85%+', color: tokens.colorPaletteRedBackground3, value: 92 },
];

const DAY_ORDER = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const formatHour = (h: number) => {
  if (h === 12) return '12pm';
  return h < 12 ? `${h}am` : `${h - 12}pm`;
};

export const UtilizationHeatmap = ({ data }: UtilizationHeatmapProps) => {
  const styles = useUtilizationHeatmapStyles();
  const { t } = useTranslation('reports');

  const chartData: IHeatMapChartData[] = useMemo(() => {
    const allPoints: IHeatMapChartDataPoint[] = data
      .sort((a, b) => DAY_ORDER.indexOf(a.day) - DAY_ORDER.indexOf(b.day) || a.hour - b.hour)
      .map((cell) => ({
        x: formatHour(cell.hour),
        y: cell.day,
        value: cell.utilization,
        rectText: `${cell.utilization}`,
      }));
    return [{ legend: 'Utilization', data: allPoints, value: 0 }];
  }, [data]);

  return (
    <WidgetContainer title={t('widgets.heatmapTitle')}>
      <div className={styles.root} role="figure" aria-label={t('widgets.heatmapTitle')}>
        <ChartThemeProvider>
        <HeatMapChart
          data={chartData}
          domainValuesForColorScale={[0, 29, 30, 49, 50, 69, 70, 84, 85, 100]}
          rangeValuesForColorScale={[
            tokens.colorPaletteGreenBackground3, tokens.colorPaletteGreenBackground3,
            tokens.colorPaletteYellowBackground3, tokens.colorPaletteYellowBackground3,
            tokens.colorPaletteMarigoldBackground3, tokens.colorPaletteMarigoldBackground3,
            tokens.colorPaletteDarkOrangeBackground3, tokens.colorPaletteDarkOrangeBackground3,
            tokens.colorPaletteRedBackground3, tokens.colorPaletteRedBackground3,
          ]}
          height={350}
          enableReflow={true}
          sortOrder="none"
          hideLegend={true}
        />
        </ChartThemeProvider>
        <div className={styles.legend}>
          {TIER_CONFIG.map(({ label, color }) => (
            <div key={label} className={styles.legendItem}>
              <span className={styles.legendSwatch} style={{ backgroundColor: color }} />
              <span className={styles.legendLabel}>{label}</span>
            </div>
          ))}
        </div>
      </div>
    </WidgetContainer>
  );
};

UtilizationHeatmap.displayName = 'UtilizationHeatmap';
